import React, {useEffect, useState} from 'react';
import {InlineAlert} from '@/components/alerts';
import CellLoader from '@/components/loading/CellLoader';
import useData from '@/utils/useData';
import {ChatEvent} from 'entities/event';
import {buildURL} from '@/utils/urlBuilder';
import i18n from 'i18n';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';

interface Props {
    eventData: ChatEvent;
}

const ChatDetailBlock = React.memo<Props>(({eventData}) => {
    const {config, loading: configLoading} = useOptimizedConfig();

    // Support both `id` and `event_id` depending on where the component is used
    const eventId = (eventData as any)?.event_id ?? (eventData as any)?.id;

    const chatURL = config && eventId
        ? buildURL('getChatConversation', { event_id: eventId })
        : null;

    const [activeChatData, loadingChat, , chatError] = useData<any>(
        chatURL, 'eventDetail', 'setActiveChatData', 'activeChatData',
    );

    // Avoid showing a "flash" of the loader for very fast responses
    const [showLoader, setShowLoader] = useState(false);

    useEffect(() => {
        let timer: number | undefined;
        if (loadingChat) {
            timer = window.setTimeout(() => setShowLoader(true), 200);
        } else {
            setShowLoader(false);
        }

        return () => {
            if (timer) {
                window.clearTimeout(timer);
            }
        };
    }, [loadingChat]);

    // Show loading while config is loading
    if (configLoading || !config) {
        return <CellLoader fullHeight={true} text={i18n.t('loading_configuration.text')} />;
    }

    const hasChatEndpoint = !!chatURL;
    const isChatLoading = hasChatEndpoint ? loadingChat : false;

    // Extract conversation array from store structure
    const chatDataArray = activeChatData?.conversation || [];


    let scrollDiv;
    if (isChatLoading && showLoader) {
        scrollDiv = (
            <CellLoader fullHeight={true} />);
    } else if (!hasChatEndpoint) {
        scrollDiv = (
            <InlineAlert
                variant="warning"
                message={i18n.t('jt_chat_not.text')}
                className="mb-3"
            />
        );
    } else if (chatError || !activeChatData || chatDataArray.length === 0) {
        scrollDiv = (
            <InlineAlert
                variant="warning"
                message={i18n.t('jt_chat_not.text')}
                className="mb-3"
            />
        );
    } else {
        scrollDiv = (
            <div className="d-flex justify-content-center">
                <div className="chat-box scrollable pl-container pl-theme-default pl-active-y custom-border chat-container p-3">
                    <ul className="chat-list m-0 p-0">
                        {
                            chatDataArray.length > 0
                                ? chatDataArray.map((x: any, index: any) => {
                                    if (x.sender_role === 'enduser') {
                                        return (
                                            <li key={index} className="odd mt-3">
                                                <div className="chat-content pl-3 d-inline-block text-right">
                                                    <h6 className="text-muted text-nowrap">{x.sender_role}</h6>
                                                    <div className="box mb-2 d-inline-block text-dark message fw-normal fs-3 bg-light-info">
                                                        {x.value}
                                                    </div>
                                                    <br />
                                                </div>
                                                <div
                                                    className="chat-time d-inline-block text-right text-dark font-weight-bolder">
                                                    {x.datetime.match(/\d{2}:\d{2}/)[0]}
                                                </div>
                                            </li>
                                        );
                                    } else if (x.sender_role === 'agent') {
                                        return (
                                            <li key={index} className="mt-3">
                                                <div className="chat-content pl-3 d-inline-block">
                                                    <h6 className="text-muted text-nowrap">{x.sender_role}</h6>
                                                    <div className="box mb-2 d-inline-block text-dark message fw-normal fs-3 bg-light-success">
                                                        {x.value}
                                                    </div>
                                                </div>
                                                <div
                                                    className="chat-time d-inline-block text-right text-dark font-weight-bolder">
                                                    {x.datetime.match(/\d{2}:\d{2}/)[0]}
                                                </div>
                                            </li>
                                        );
                                    } else if (x.sender_role === 'bot') {
                                        return (
                                            <li key={index} className="mt-3">
                                                <div className="chat-content pl-3 d-inline-block">
                                                    <h6 className="text-muted text-nowrap">{x.sender_role}</h6>
                                                    <div className="box mb-2 d-inline-block text-dark message fw-normal fs-3 bg-light-primary">
                                                        {x.value}
                                                    </div>
                                                </div>
                                                <div
                                                    className="chat-time d-inline-block text-right text-dark font-weight-bolder">
                                                    {x.datetime.match(/\d{2}:\d{2}/)[0]}
                                                </div>
                                            </li>
                                        );
                                    } else {
                                        if (x['options']) {
                                            return (
                                                <li key={index} className="mt-3">
                                                    <div className="chat-content pl-3 d-inline-block">
                                                        <h6 className="text-muted text-nowrap">{i18n.t('buttons.text')}</h6>
                                                        {x['options'].map((entry: any, optionIndex: any) => (
                                                            <div key={optionIndex} className="box mb-2 mr-2 d-inline-block text-dark message fw-normal fs-3 bg-light-inverse">
                                                                {entry}
                                                            </div>))}
                                                    </div>
                                                    <div
                                                        className="chat-time d-inline-block text-right text-dark font-weight-bolder">
                                                        {x.datetime.match(/\d{2}:\d{2}/)[0]}
                                                    </div>
                                                </li>
                                            );
                                        }
                                        return (
                                            <li key={index} className="odd mt-3">
                                                <div className="chat-content pl-3 d-inline-block text-right">
                                                    <h6 className="text-muted text-nowrap">{x.sender_role}</h6>
                                                    <div className="box mb-2 d-inline-block text-dark message fw-normal fs-3 bg-light-info">
                                                        {x.value}
                                                    </div>
                                                    <br />
                                                </div>
                                                <div
                                                    className="chat-time d-inline-block text-right text-dark font-weight-bolder">
                                                    {x.datetime.match(/\d{2}:\d{2}/)[0]}
                                                </div>
                                            </li>
                                        );
                                    }
                                }
                                ) : null
                        }
                    </ul>
                </div>
            </div>
        );
    }
    return (
        <div>
            {scrollDiv}
        </div>
    );
});
export default ChatDetailBlock;